import axios from 'axios'
import { crearErrorAplicacionApi, esErrorApiAplicacion } from './apiError'
import { Usuario, UsuarioLogin, UsuarioCrear, Token } from '../types'

// Configuración base de axios
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
})

// Interceptor para agregar el token a las peticiones
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('logixport_token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Interceptor para manejar errores de respuesta
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const pathname = window.location.pathname
      // Si estamos en la página de login, nunca redirigir ni limpiar
      if (pathname.startsWith('/login')) {
        return Promise.reject(error)
      }

      const url = error.config?.url || ''
      const method = (error.config?.method || '').toLowerCase()
      const isLoginAttempt = method === 'post' && url.includes('/auth/login')

      // No forzar redirección en intento de login: dejar que la UI muestre el error
      if (isLoginAttempt) {
        return Promise.reject(error)
      }

      // Token expirado o inválido en otras rutas: limpiar y enviar a /login
      localStorage.removeItem('logixport_token')
      localStorage.removeItem('logixport_user')
      if (!pathname.startsWith('/login')) {
        window.location.href = '/login'
      }
    }
    return Promise.reject(error)
  }
)

export const authService = {
  normalizarUsuario(apiUser: Record<string, unknown>): Usuario {
    const rolGlobalRaw = String(apiUser.rol_global ?? apiUser.global_role ?? apiUser.rol ?? 'usuario')
    const rolGlobal: Usuario['rol_global'] =
      rolGlobalRaw === 'superadmin'
        ? 'superadmin'
        : rolGlobalRaw === 'admin'
          ? 'admin'
          : 'usuario'
    const rolOrganizacion = apiUser.rol_organizacion ? String(apiUser.rol_organizacion) : undefined
    const tipoOrganizacion = apiUser.tipo_organizacion ? String(apiUser.tipo_organizacion) : undefined
    const rawRol =
      apiUser.rol ??
      (tipoOrganizacion && rolOrganizacion ? `${tipoOrganizacion}_${rolOrganizacion}` : rolGlobal)
    let mappedRol: Usuario['rol'] = 'user'

    if (rawRol === 'superadmin') {
      mappedRol = 'superadmin'
    } else if (rawRol === 'admin') {
      mappedRol = 'admin'
    } else if (String(rawRol).startsWith('institucion') || String(rawRol).startsWith('empresa_')) {
      mappedRol = String(rawRol) as Usuario['rol']
    }

    return {
      id: String(apiUser.id_usuario ?? apiUser.id ?? ''),
      email: String(apiUser.correo ?? apiUser.email ?? ''),
      nombre: String(apiUser.nombre ?? apiUser.nombre_usuario ?? 'Usuario'),
      apellido: String(apiUser.apellido ?? ''),
      avatar: apiUser.avatar ? String(apiUser.avatar) : undefined,
      telefono: apiUser.telefono ? String(apiUser.telefono) : undefined,
      id_organizacion:
        typeof apiUser.id_organizacion === 'number'
          ? apiUser.id_organizacion
          : typeof apiUser.id_organizacion === 'string' && apiUser.id_organizacion.trim() !== ''
            ? Number(apiUser.id_organizacion)
            : undefined,
      empresa: apiUser.empresa ? String(apiUser.empresa) : undefined,
      rol_global: rolGlobal,
      rol_organizacion: rolOrganizacion,
      tipo_organizacion: tipoOrganizacion,
      rol: mappedRol,
      activo: Boolean(apiUser.activo ?? true),
      fechaCreacion: String(apiUser.fecha_creacion ?? new Date().toISOString()),
      ultimoAcceso: apiUser.ultimo_ingreso ? String(apiUser.ultimo_ingreso) : undefined,
      membresia_activa: apiUser.membresia_activa === undefined ? undefined : Boolean(apiUser.membresia_activa),
      plan_membresia: apiUser.plan_membresia ? String(apiUser.plan_membresia) : undefined,
      fecha_expiracion_membresia: apiUser.fecha_expiracion_membresia
        ? String(apiUser.fecha_expiracion_membresia)
        : undefined,
      permisos: Array.isArray(apiUser.permisos) ? (apiUser.permisos as string[]) : []
    }
  },

  /**
   * Iniciar sesión con email y contraseña
   */
  async login(credentials: UsuarioLogin): Promise<Token> {
    try {
      // El backend espera JSON: { email, password, remember_me }
      const response = await api.post('/auth/login', {
        email: credentials.email,
        password: credentials.password,
        remember_me: !!credentials.recordarme,
      })
      return response.data
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al iniciar sesión')
    }
  },

  /**
   * Iniciar sesión con Google
   */
  async loginGoogle(credential: string, client_id?: string): Promise<Token> {
    try {
      const response = await api.post('/auth/login/google', {
        credential,
        client_id
      })
      return response.data
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al iniciar sesión con Google')
    }
  },

  /**
   * Registrar nuevo usuario sin requerir pago inmediato.
   * El backend asigna plan básico por defecto y crea la organización si aplica.
   */
  async register(userData: UsuarioCrear): Promise<{
    message: string
    id_usuario: number
    correo: string
    nombre_usuario: string
    plan_membresia: string
    membresia_activa: boolean
    tipo_organizacion?: string
    rol_organizacion?: string
    id_organizacion?: number
  }> {
    try {
      const payload = {
        email: userData.email,
        password: userData.password,
        nombre: userData.nombre,
        apellido: userData.apellido,
        telefono: userData.telefono,
        empresa: userData.empresa,
        tipo_organizacion: userData.tipo_organizacion,
        nombre_organizacion: userData.nombre_organizacion,
      }
      const response = await api.post('/api/users', payload)
      return response.data
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al registrar usuario')
    }
  },

  /**
   * Enviar notificación de bienvenida por email.
   * Si el backend NO tiene configurado SMTP, se devuelve success=false pero NO lanza
   * excepción para no romper el flujo de registro.
   */
  async sendWelcomeEmail(
    email: string,
    nombre?: string,
    planMembresia?: string,
    urlLogin?: string,
  ): Promise<void> {
    const body = {
      email,
      nombre,
      plan_membresia: planMembresia ?? 'basica',
      url_login: urlLogin ?? `${window.location.origin}/login`,
    }
    const endpointsAProbar = [
      '/api/configuracion/notifications/welcome',
      '/api/configuracion/email/notifications/welcome',
      '/api/notifications/welcome',
    ]

    for (let i = 0; i < endpointsAProbar.length; i++) {
      const url = endpointsAProbar[i]
      const esUltimo = i === endpointsAProbar.length - 1
      try {
        const respuesta = await api.post<{
          success: boolean
          skipped?: boolean
          message?: string
        }>(url, body)
        if (respuesta.data?.success === false) {
          console.warn('[welcome-email] Skipped:', respuesta.data.message || 'Servicio SMTP no configurado')
        }
        return
      } catch (error: any) {
        const status = error?.response?.status
        const is404 = status === 404
        console.warn(
          `[welcome-email] Endpoint ${url} ${is404 ? 'no existe' : `falló (status=${status ?? 'N/A'})`}${esUltimo ? '' : ', probando siguiente'}`,
          error?.message ?? error,
        )
        if (!is404 && esUltimo) {
          console.warn('[welcome-email] No se pudo enviar email de bienvenida.')
        }
      }
    }
  },

  /**
   * Verificar disponibilidad de email
   */
  async checkEmailAvailability(email: string): Promise<boolean> {
    try {
      const response = await api.get(`/api/users/check-email?email=${encodeURIComponent(email)}`)
      return response.data.available
    } catch (error: any) {
      // Si hay error, asumimos que el email no está disponible
      return false
    }
  },

  /**
   * Obtener información del usuario actual
   */
  async getCurrentUser(): Promise<Usuario> {
    try {
      const response = await api.get('/auth/me')
      return this.normalizarUsuario((response.data || {}) as Record<string, unknown>)
    } catch (error: unknown) {
      throw crearErrorAplicacionApi(error, 'Error al obtener información del usuario')
    }
  },

  /**
   * Verificar si el token es válido
   */
  async verifyToken(): Promise<boolean> {
    try {
      await this.getCurrentUser()
      return true
    } catch (error) {
      return false
    }
  },

  /**
   * Solicitar restablecimiento de contraseña (FORGOT PASSWORD).
   * Siempre resuelve exitosamente por seguridad (no revela si el correo existe).
   */
  async requestPasswordReset(email: string): Promise<{
    success: boolean
    message: string
    email_enviado: boolean
    email_detalle?: string
    url_reset?: string
  }> {
    try {
      const response = await api.post('/api/auth/password/request-reset', { email })
      return response.data
    } catch (error: unknown) {
      if (esErrorApiAplicacion(error) && error.code === 'unauthorized') {
        throw error
      }
      return {
        success: false,
        message: 'No se pudo procesar la solicitud en este momento. Inténtalo de nuevo.',
        email_enviado: false,
      }
    }
  },

  /**
   * Confirmar restablecimiento de contraseña con el token recibido por email.
   */
  async confirmPasswordReset(
    token: string,
    newPassword: string,
    confirmPassword: string,
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post('/api/auth/password/reset', {
      token,
      new_password: newPassword,
      confirm_password: confirmPassword,
    })
    return response.data
  },

  /**
   * Cerrar sesión (limpiar datos locales)
   */
  logout(): void {
    localStorage.removeItem('logixport_token')
    localStorage.removeItem('logixport_user')
  },

  /**
   * Verificar si el usuario está autenticado
   */
  isAuthenticated(): boolean {
    const token = localStorage.getItem('logixport_token')
    return !!token
  },

  /**
   * Obtener el token actual
   */
  getToken(): string | null {
    return localStorage.getItem('logixport_token')
  },

  /**
   * Obtener datos del usuario desde localStorage
   */
  getUserFromStorage(): Usuario | null {
    const userData = localStorage.getItem('logixport_user')
    if (userData) {
      return this.normalizarUsuario(JSON.parse(userData) as Record<string, unknown>)
    }
    return null
  }
}

export default api
